import {
  INCOMLEND_INTERNAL,
  INCOMLEND_INTERNAL_NAME,
  SELECT_ACCOUNT,
} from "codes";
import { isUUID } from "helpers/redirect";

const UUID_PATTERN =
  /[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}/;

/** App permission while an internal user is on the /accounts picker */
export const ACCOUNTS_LIST_PERMISSION = "Accounts";

/** Role-switcher values that are not database account ids */
const RESERVED_ACCOUNT_API_IDS = new Set([
  ACCOUNTS_LIST_PERMISSION,
  "Funders",
  "Reports",
  INCOMLEND_INTERNAL,
]);

/** Label for the internal role in the account switcher dropdown */
export const INCOMLEND_INTERNAL_VIEW_LABEL = `${INCOMLEND_INTERNAL_NAME}`;

/** Non-selectable MUI Select header value (must not match any real role id) */
export const ROLE_SWITCHER_HEADER_VALUE = "__role_switcher_context__";

export type ViewContextInput = {
  appPermission?: string;
  appState?: string;
};

export const isAccountListView = ({
  appPermission = "",
  appState = "",
}: ViewContextInput = {}): boolean =>
  appPermission === ACCOUNTS_LIST_PERMISSION || appState === SELECT_ACCOUNT;

export const isActingAsClientAccount = ({
  appPermission = "",
  appState = "",
}: ViewContextInput = {}): boolean =>
  appPermission === "Client" ||
  UUID_PATTERN.test(appPermission) ||
  isAccountListView({ appPermission, appState });

export const isInternalViewActive = ({
  appPermission = "",
  appState = "",
}: ViewContextInput = {}): boolean =>
  appPermission === INCOMLEND_INTERNAL &&
  !isAccountListView({ appPermission, appState });

/** True only when id is a UUID suitable for GET /api/v2/accounts/:id */
export const isResolvableAccountApiId = (accountId?: string | null): boolean =>
  Boolean(accountId) &&
  !RESERVED_ACCOUNT_API_IDS.has(accountId) &&
  isUUID.test(accountId);

/** Merge RTK skip with invalid / reserved account ids */
export const shouldSkipAccountApiQuery = (
  accountId?: string | null,
  extraSkip = false,
): boolean => extraSkip || !isResolvableAccountApiId(accountId);

export type RoleSwitcherMenuOption = {
  id: string;
  name?: string;
};

/** Label shown in the profile role Select (value may be a UUID not listed in menu items). */
export const getRoleSwitcherDisplayLabel = (
  selectedId: string,
  {
    appPermission = "",
    appState = "",
    roleName = "",
    companyName = "",
    menuOptions = [],
  }: ViewContextInput & {
    roleName?: string;
    companyName?: string;
    menuOptions?: RoleSwitcherMenuOption[];
  } = {},
): string => {
  if (isAccountListView({ appPermission, appState })) {
    return ACCOUNTS_LIST_PERMISSION;
  }
  if (selectedId === INCOMLEND_INTERNAL) {
    return INCOMLEND_INTERNAL_VIEW_LABEL;
  }
  if (selectedId === ACCOUNTS_LIST_PERMISSION) {
    return "Accounts";
  }
  if (selectedId === "Funders") {
    return "Funders";
  }
  if (selectedId === "Reports") {
    return "Reports";
  }

  const menuRole = menuOptions.find((role) => role.id === selectedId);
  if (menuRole?.name) {
    return menuRole.id === INCOMLEND_INTERNAL
      ? INCOMLEND_INTERNAL_VIEW_LABEL
      : menuRole.name;
  }

  if (roleName) {
    return roleName;
  }

  if (companyName) {
    return companyName;
  }

  return "";
};
